import React, { useState } from 'react';
import { 
  Modal, 
  Button, 
  Alert, 
  Typography, 
  message,
  Space,
  Divider
} from 'antd';
import { 
  GoogleOutlined,
  HomeOutlined,
  CheckCircleOutlined,
  StarOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../styles/theme';

const { Title, Text } = Typography;

const AuthModal = ({ visible, onClose, referralCode = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { signInWithGoogle } = useAuth();

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await signInWithGoogle(referralCode);
      if (result) {
        // If popup was successful, close modal and show success message
        message.success('Welcome to HSP!');
        onClose();
      }
      // If redirect was used, the user will be redirected and modal will close automatically
    } catch (error) {
      setError(getErrorMessage(error.code));
      setLoading(false);
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled. Please try again.';
      case 'auth/cancelled-popup-request':
        return 'Sign-in was cancelled. Please try again.';
      case 'auth/popup-blocked':
        return 'Popup was blocked by your browser. Please allow popups and try again.';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email address.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      default:
        return 'An error occurred during sign-in. Please try again.';
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      width="90%"
      centered
      style={{
        top: 20,
        maxWidth: '480px'
      }}
      styles={{
        body: {
          padding: 0,
          background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%)`,
          borderRadius: theme.borderRadius.xl,
          overflow: 'hidden'
        },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)'
        }
      }}
    >
      <div style={{
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%)`,
        padding: window.innerWidth < 768 ? '32px 20px' : '40px 32px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decoration */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '120px',
          height: '120px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '80px',
          height: '80px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          zIndex: 0
        }} />

        {/* Close button */}
        <Button
          type="text"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: window.innerWidth < 768 ? '12px' : '16px',
            right: window.innerWidth < 768 ? '12px' : '16px',
            color: theme.colors.white,
            fontSize: window.innerWidth < 768 ? '16px' : '18px',
            width: window.innerWidth < 768 ? '28px' : '32px',
            height: window.innerWidth < 768 ? '28px' : '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            zIndex: 1,
            minWidth: 'auto'
          }}
        >
          ×
        </Button>

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Logo and title */}
          <div style={{ marginBottom: window.innerWidth < 768 ? '24px' : '32px' }}>
            <div style={{
              width: window.innerWidth < 768 ? '60px' : '80px',
              height: window.innerWidth < 768 ? '60px' : '80px',
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.2)'
            }}>
              <HomeOutlined style={{ 
                fontSize: window.innerWidth < 768 ? '24px' : '32px', 
                color: theme.colors.white 
              }} />
            </div>
            <Title 
              level={2} 
              style={{ 
                margin: 0, 
                color: theme.colors.white,
                fontFamily: theme.fonts.primary,
                fontWeight: 700,
                fontSize: window.innerWidth < 768 ? '24px' : '28px'
              }}
            >
              Welcome to HSP
            </Title>
            <Text 
              style={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: window.innerWidth < 768 ? '14px' : '16px',
                fontFamily: theme.fonts.primary,
                display: 'block',
                marginTop: '8px'
              }}
            >
              Your trusted home maintenance partner
            </Text>
          </div>

          {/* Error message */}
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              style={{ 
                marginBottom: '24px',
                borderRadius: theme.borderRadius.lg,
                border: 'none',
                background: 'rgba(255, 77, 79, 0.1)',
                backdropFilter: 'blur(10px)'
              }}
            />
          )}

          {/* Referral message */}
          {referralCode && (
            <div style={{ 
              background: 'rgba(82, 196, 26, 0.15)', 
              border: '1px solid rgba(82, 196, 26, 0.3)', 
              borderRadius: theme.borderRadius.lg, 
              padding: '16px', 
              marginBottom: '24px',
              backdropFilter: 'blur(10px)'
            }}>
              <Space>
                <StarOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                <Text style={{ color: '#52c41a', fontWeight: '500', fontSize: '14px' }}>
                  You've been referred by a friend! You'll both earn rewards when you sign up.
                </Text>
              </Space>
            </div>
          )}

          {/* Benefits */}
          <div style={{ marginBottom: window.innerWidth < 768 ? '24px' : '32px' }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <CheckCircleOutlined style={{ 
                  color: theme.colors.white, 
                  fontSize: window.innerWidth < 768 ? '14px' : '16px' 
                }} />
                <Text style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  fontSize: window.innerWidth < 768 ? '13px' : '14px' 
                }}>
                  Access to all home maintenance services
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <CheckCircleOutlined style={{ 
                  color: theme.colors.white, 
                  fontSize: window.innerWidth < 768 ? '14px' : '16px' 
                }} />
                <Text style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  fontSize: window.innerWidth < 768 ? '13px' : '14px' 
                }}>
                  Track your service history and quotes
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <CheckCircleOutlined style={{ 
                  color: theme.colors.white, 
                  fontSize: window.innerWidth < 768 ? '14px' : '16px' 
                }} />
                <Text style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  fontSize: window.innerWidth < 768 ? '13px' : '14px' 
                }}>
                  Get personalized recommendations
                </Text>
              </div>
            </Space>
          </div>

          {/* Sign in button */}
          <Button
            onClick={handleGoogleSignIn}
            loading={loading}
            size="large"
            style={{
              height: window.innerWidth < 768 ? '48px' : '56px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: window.innerWidth < 768 ? '8px' : '12px',
              background: theme.colors.white,
              border: 'none',
              color: theme.colors.black,
              fontSize: window.innerWidth < 768 ? '14px' : '16px',
              fontWeight: '600',
              borderRadius: theme.borderRadius.lg,
              boxShadow: theme.shadows.lg,
              fontFamily: theme.fonts.primary,
              transition: 'all 0.3s ease',
              marginBottom: window.innerWidth < 768 ? '20px' : '24px',
              minWidth: 'auto'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 12px 24px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = theme.shadows.lg;
            }}
          >
            <GoogleOutlined style={{ 
              fontSize: window.innerWidth < 768 ? '18px' : '20px', 
              color: '#4285f4' 
            }} />
            Continue with Google
          </Button>

          {/* Terms */}
          <div style={{ textAlign: 'center' }}>
            <Text 
              style={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                fontSize: window.innerWidth < 768 ? '11px' : '12px',
                fontFamily: theme.fonts.primary,
                lineHeight: window.innerWidth < 768 ? '1.4' : '1.5'
              }}
            >
              By signing in, you agree to our{' '}
              <Button 
                type="link" 
                style={{ 
                  padding: 0, 
                  fontSize: window.innerWidth < 768 ? '11px' : '12px',
                  color: theme.colors.white,
                  textDecoration: 'underline',
                  height: 'auto',
                  minWidth: 'auto'
                }}
              >
                Terms of Service
              </Button>{' '}
              and{' '}
              <Button 
                type="link" 
                style={{ 
                  padding: 0, 
                  fontSize: window.innerWidth < 768 ? '11px' : '12px',
                  color: theme.colors.white,
                  textDecoration: 'underline',
                  height: 'auto',
                  minWidth: 'auto'
                }}
              >
                Privacy Policy
              </Button>
            </Text>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AuthModal;
